import { CenteredContentLayout } from 'components/containers/CenteredContentLayout';
import React, { Dispatch, SetStateAction } from 'react';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import { ButtonMenu } from 'components/actions/ButtonMenu';
import { ContainerListItem } from 'components/actions/ListItem/ListItem.view';
import classNames from 'classnames';
import classes from './AutoImporters.module.scss';
import { ImporterCard } from './ImporterCard';
import { CreateEditImporterModal } from './CreateEditImporterModal';
import { DeleteImporterModal } from './DeleteImporterModal';
import { PauseImporterModal } from './PauseImporterModal';
import { GetAutoImportsQuery } from 'models/graphql';
import { MappedProfileType } from 'components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { CreateEditImporterModalType, ModalState } from './AutoImporters.controller';
import { EmptyPlaceholder } from '../../../components/containers/EmptyPlaceholder';
import noRssImporters from 'assets/images/noRssImporters.svg';
import { useNavigate } from 'react-router-dom';
import { BiggerScreenPlaceholder } from '../../../components/containers/BiggerScreenPlaceHolder';
import { usePauseSettings } from 'hooks/usePauseSettings';

export type AutoImportQueryResult = GetAutoImportsQuery['workspace']['autoImports'][number];
export type CategoriesResult = GetAutoImportsQuery['workspace']['categories'][number];

export type AutoImportersViewProps = {
  deleteModalState: ModalState;
  pauseModalState: ModalState;
  openDeleteModal: (title?: string, id?: string) => void;
  closeDeleteModal: VoidFunction;
  openPauseModal: (importer: AutoImportQueryResult) => void;
  closePauseModal: VoidFunction;
  selectedImporterEdit: AutoImportQueryResult | undefined;
  setSelectedImporterEdit: (importer: undefined | AutoImportQueryResult) => void;
  autoImporters: AutoImportQueryResult[];
  profileCategories: CategoriesResult[];
  socialProfiles: MappedProfileType[];
  loading: boolean;
  shouldNotShowPage: boolean;
  createEditImporterModalState: CreateEditImporterModalType;
  setCreateEditImporterModalState: Dispatch<SetStateAction<CreateEditImporterModalType>>;
};

type ThirdNavRightButtonsType = {
  onCreateAutoImporter: VoidFunction;
  openPauseModal: VoidFunction;
  openDeleteModal: VoidFunction;
  autoImporters: AutoImportQueryResult[];
  isImportersPaused: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ThirdNavRightButtons = ({
  onCreateAutoImporter,
  openPauseModal,
  openDeleteModal,
  autoImporters,
  isImportersPaused
}: ThirdNavRightButtonsType): JSX.Element | null => {
  if (!autoImporters) {
    return null;
  }
  const shouldShowButtonGroup = autoImporters.length !== 0;

  const ButtonMenuListItems: ContainerListItem[] = [
    {
      action: openPauseModal,
      label: isImportersPaused ? 'Un-pause all Importing' : 'Pause all Importing'
    },
    {
      action: openDeleteModal,
      label: 'Delete all'
    }
  ];

  return (
    <div className={'grid-x align-right'}>
      <div className={classNames(classes.MarginButton, 'hide-for-small-only')}>
        {shouldShowButtonGroup && (
          <ButtonMenu
            isSquareButton={false}
            elementType={'button'}
            variant={'hollow'}
            listItems={ButtonMenuListItems}
            side={'right'}
            size={'medium'}
            icon={IconsEnum.threeDot}
          />
        )}
      </div>
      <div className={'hide-for-small-only'}>
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          icon={{ side: 'left', value: IconsEnum.plus }}
          action={onCreateAutoImporter}>
          Add Auto-Importer
        </Button>
      </div>
    </div>
  );
};

export const AutoImporterView = ({
  deleteModalState,
  pauseModalState,
  openDeleteModal,
  closeDeleteModal,
  openPauseModal,
  closePauseModal,
  createEditImporterModalState,
  setCreateEditImporterModalState,
  autoImporters,
  loading,
  shouldNotShowPage
}: AutoImportersViewProps): JSX.Element => {
  const navigate = useNavigate();

  const { autoImportersPaused } = usePauseSettings();

  if (shouldNotShowPage) {
    return <BiggerScreenPlaceholder />;
  }
  return (
    <CenteredContentLayout loading={loading}>
      {Object.keys(autoImporters).length > 0 ? (
        autoImporters.map(importer => (
          <ImporterCard
            key={importer.id}
            autoImporter={importer}
            openModalDelete={() => openDeleteModal(importer.name, importer.id)}
            openModalPause={() => openPauseModal(importer)}
            openModalEdit={() => setCreateEditImporterModalState({ isOpen: true, autoImporter: importer })}
          />
        ))
      ) : (
        <EmptyPlaceholder
          title={'You don’t have any RSS Importers'}
          image={noRssImporters}
          buttonTitle={'Add Your First Importer'}
          buttonIcon={IconsEnum.plus}
          buttonAction={() => navigate('/discover/find', { state: { context: 'selectRSS' } })}>
          <div>
            Auto-Importers automatically pull new content from RSS feeds and turn them into social posts—saving you time
            and keeping your Queue fresh!{/* @TODO: Copy */}
          </div>
        </EmptyPlaceholder>
      )}
      {createEditImporterModalState.isOpen && (
        <CreateEditImporterModal
          onClose={() => {
            setCreateEditImporterModalState({ isOpen: false, autoImporter: undefined });
          }}
          autoImporter={createEditImporterModalState.autoImporter}
        />
      )}
      {deleteModalState.isOpen && (
        <DeleteImporterModal
          onClose={closeDeleteModal}
          importerTitle={deleteModalState.title}
          id={deleteModalState.id}
        />
      )}
      {pauseModalState.isOpen && (
        <PauseImporterModal
          onClose={closePauseModal}
          importer={pauseModalState.importer}
          isImportersPaused={autoImportersPaused}
        />
      )}
    </CenteredContentLayout>
  );
};
